/*
  Roots motion — the count settles
  ================================

  WHAT THIS IS FOR. On a storefront with a drawer cart, the number in the header
  IS the receipt: it is the only thing that confirms an add-to-cart landed once
  the drawer closes. It changed instantly, which made the single state change a
  shopper is actively watching for the one with no acknowledgement at all.

  WHY AN OBSERVER AND NOT SIX PATCHES. Six different scripts update that number —
  `cart-drawer`, `cart-notification`, `cart`, `quick-add-bulk`, `quick-order-list`
  and the product form — and every one of them does it the same way, by asking
  the Section Rendering API for `cart-icon-bubble` and replacing the section's
  innerHTML. So the bubble is not mutated, it is REPLACED, and the one thing all
  six paths have in common is that replacement. Watching the container catches
  every path, including any added later, without a line of motion code living in
  a cart script.

  It also gets the load case right for free: an observer cannot fire for markup
  that arrived with the document, so the count animates when it changes and stays
  still when the page simply has one.

  The animation itself, and its `prefers-reduced-motion` opt-out, live in
  `component-roots-motion.css`. This file only says WHEN.
*/

(() => {
  const CHANGED = 'data-roots-count-changed';

  /*
    Stamping the attribute is not enough on its own when the same element is
    stamped twice in a row — a CSS animation does not restart because an
    attribute was set again. The attribute is cleared on `animationend` so the
    next change re-triggers it, and a shopper adding two items in quick
    succession sees two beats rather than one.
  */
  const mark = (el) => {
    if (!el) return;
    el.removeAttribute(CHANGED);
    // Force a style recalculation so the removal and the re-add are two frames
    // as far as the animation is concerned, not one no-op.
    void el.offsetWidth;
    el.setAttribute(CHANGED, '');
  };

  document.addEventListener(
    'animationend',
    (event) => {
      if (event.animationName === 'roots-count-settle') {
        event.target.removeAttribute(CHANGED);
      }
    },
    true
  );

  /*
    `#cart-icon-bubble` is the cart LINK in the header, not a section wrapper —
    this theme renders the cart icon inside the header section rather than as a
    section of its own. Every cart script resolves the same id through
    `document.getElementById('cart-icon-bubble')` and overwrites its innerHTML,
    so the link is both the stable node and the one that actually mutates.

    The bubble itself cannot be the observed node: it is absent whenever the cart
    is empty, and the change worth marking is precisely the one that creates it.
  */
  const cartIcon = document.getElementById('cart-icon-bubble');

  if (cartIcon) {
    const observer = new MutationObserver(() => {
      mark(cartIcon.querySelector('.cart-count-bubble'));
    });

    observer.observe(cartIcon, { childList: true, subtree: true });
  }

  /*
    The saved count is a custom element that re-renders in place rather than
    being replaced, so it announces its own changes. `roots-wishlist.js` fires
    this after it has written the new number.
  */
  document.addEventListener('roots:count-changed', (event) => {
    mark(event.target);
  });
})();
